import { useAuth0 } from '@auth0/auth0-react'
import { Avatar, MenuList, ListItem, Divider } from '@clickguard/design-system'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { removeFromLocalStorage } from 'utils'
import { clearFiltersFromStorage } from 'utils/filter-presistence'
import { clearMarketingParams } from 'utils/marketing-params'
import { useStores } from 'hooks'
import { ChatService } from 'services/chat'
import { AppConfig } from 'config'

export const ProfileDropdown = () => {
	const [isOpen, setIsOpen] = useState(false)
	const { accountStore, userStore } = useStores()
	const { user } = userStore
	const dropdownRef = useRef<HTMLDivElement>(null)
	const { logout } = useAuth0()
	const navigate = useNavigate()
	const initials = (
		(user?.name?.[0] || '') + (user?.name?.[1] || '') || 'JD'
	).toUpperCase()
	const removeImpersonationId = () => {
		removeFromLocalStorage(['selected_account', 'selected_asset'])
		if (localStorage.getItem('imper_userId')) {
			localStorage.removeItem('imper_userId')
		}
	}
	const { accountId } = useParams()
	const { accounts } = accountStore
	const isNewWorkspace = useMemo(() => {
		if (accountId) {
			return accounts.length === 1
		}
		return accounts.length === 0
	}, [accountId, accounts])

	useEffect(() => {
		const handleClickOutside = (e: MouseEvent) => {
			if (
				dropdownRef.current &&
				!dropdownRef.current.contains(e.target as Node)
			) {
				setIsOpen(false)
			}
		}
		document.addEventListener('mousedown', handleClickOutside)
		return () => document.removeEventListener('mousedown', handleClickOutside)
	}, [])

	return (
		<div className="relative" ref={dropdownRef}>
			<button
				type="button"
				onClick={() => setIsOpen((prev) => !prev)}
				className="p-[4px] gap-[4px] rounded-full bg-[rgba(255,255,255,0.10)] flex flex-row items-center justify-center cursor-pointer"
			>
				<Avatar initials={initials} size="sm" tone="purple-solid" />
				<svg
					width="16"
					height="16"
					viewBox="0 0 16 16"
					fill="none"
					xmlns="http://www.w3.org/2000/svg"
					className={`transition-transform ${isOpen ? 'rotate-180' : ''}`}
				>
					<path
						d="M4 6L8 10L12 6"
						stroke="white"
						strokeWidth="1.25"
						strokeLinecap="round"
						strokeLinejoin="round"
					/>
				</svg>
			</button>
			{isOpen && (
				<div className="absolute right-0 top-full mt-2 z-50">
					<MenuList>
						<div className="py-[8px] gap-[12px] flex flex-row items-center justify-start">
							<Avatar initials={initials} size="md" tone="purple-solid" />
							<div className="flex flex-col items-start justify-start">
								<p className="text-md font-bold leading-[22px] text-t-coupon">
									{user?.name}
								</p>
								<p className="text-sm font-medium text-t-desc-gray leading-[18px] w-[200px] truncate">
									{user?.email}
								</p>
							</div>
						</div>
						<Divider className="py-[2px]" />
						{!isNewWorkspace && (
							<>
								<ListItem
									leadingIcon={
										<svg
											width="20"
											height="20"
											viewBox="0 0 20 20"
											fill="none"
											xmlns="http://www.w3.org/2000/svg"
										>
											<g clip-path="url(#clip0_8491_4738)">
												<path
													d="M7.49935 7.50033H7.50768M12.4993 12.5003H12.5077M13.3327 6.66699L6.66602 13.3337M14.917 4.16575C15.0886 4.58077 15.418 4.91066 15.8327 5.08289L17.287 5.68531C17.7021 5.85723 18.0318 6.18699 18.2037 6.60204C18.3757 7.01709 18.3757 7.48344 18.2037 7.8985L17.6018 9.35183C17.4298 9.76706 17.4295 10.2339 17.6023 10.6489L18.2032 12.1018C18.2885 12.3074 18.3324 12.5277 18.3324 12.7503C18.3324 12.9728 18.2886 13.1932 18.2035 13.3988C18.1183 13.6044 17.9935 13.7912 17.8361 13.9485C17.6787 14.1058 17.4919 14.2306 17.2863 14.3157L15.833 14.9177C15.418 15.0893 15.0881 15.4187 14.9159 15.8335L14.3135 17.2878C14.1416 17.7029 13.8118 18.0327 13.3968 18.2046C12.9817 18.3765 12.5154 18.3765 12.1004 18.2046L10.6471 17.6026C10.232 17.4311 9.76587 17.4314 9.35108 17.6036L7.89676 18.2051C7.48195 18.3766 7.01602 18.3765 6.60132 18.2047C6.18662 18.0329 5.85706 17.7036 5.68503 17.289L5.08245 15.8342C4.91085 15.4191 4.58147 15.0892 4.16672 14.917L2.7124 14.3146C2.29754 14.1427 1.9679 13.8132 1.79592 13.3984C1.62394 12.9836 1.62371 12.5174 1.79527 12.1024L2.39724 10.6491C2.56873 10.2341 2.56838 9.76787 2.39626 9.35306L1.79516 7.89765C1.70993 7.69207 1.66605 7.47172 1.66602 7.24919C1.66598 7.02665 1.70979 6.80628 1.79495 6.60069C1.88011 6.39509 2.00494 6.20829 2.16232 6.05095C2.3197 5.89362 2.50654 5.76884 2.71215 5.68375L4.16543 5.08176C4.58007 4.9103 4.90975 4.58133 5.0821 4.16704L5.6845 2.71267C5.85641 2.29761 6.18616 1.96785 6.6012 1.79593C7.01624 1.62401 7.48257 1.62401 7.89761 1.79593L9.35089 2.39792C9.76594 2.56942 10.2321 2.56907 10.6469 2.39695L12.1018 1.79687C12.5168 1.62504 12.983 1.62508 13.398 1.79696C13.813 1.96885 14.1427 2.29852 14.3146 2.71346L14.9172 4.16827L14.917 4.16575ZM7.91602 7.50033C7.91602 7.73044 7.72947 7.91699 7.49935 7.91699C7.26923 7.91699 7.08268 7.73044 7.08268 7.50033C7.08268 7.27021 7.26923 7.08366 7.49935 7.08366C7.72947 7.08366 7.91602 7.27021 7.91602 7.50033ZM12.916 12.5003C12.916 12.7304 12.7295 12.917 12.4993 12.917C12.2692 12.917 12.0827 12.7304 12.0827 12.5003C12.0827 12.2702 12.2692 12.0837 12.4993 12.0837C12.7295 12.0837 12.916 12.2702 12.916 12.5003Z"
													style={{ stroke: 'var(--t-desc-gray)' }}
													stroke-width="1.5"
													stroke-linecap="round"
													stroke-linejoin="round"
												/>
											</g>
											<defs>
												<clipPath id="clip0_8491_4738">
													<rect width="20" height="20" fill="white" />
												</clipPath>
											</defs>
										</svg>
									}
									label="Offers"
									onClick={() => {
										navigate('/settings/user/offers')
										setIsOpen(false)
									}}
								/>
								<ListItem
									leadingIcon={
										<svg
											xmlns="http://www.w3.org/2000/svg"
											width="17"
											height="16"
											viewBox="0 0 17 16"
											fill="none"
										>
											<path
												d="M16.2567 7.83333C16.2567 11.7454 13.0854 14.9167 9.17335 14.9167C8.27604 14.9167 7.41769 14.7498 6.62765 14.4454C6.4832 14.3898 6.41098 14.362 6.35355 14.3487C6.29706 14.3357 6.25616 14.3303 6.19824 14.3281C6.13934 14.3258 6.07472 14.3325 5.94549 14.3458L1.67797 14.787C1.2711 14.829 1.06767 14.8501 0.947665 14.7769C0.843142 14.7131 0.771951 14.6066 0.753001 14.4856C0.731244 14.3468 0.828457 14.1668 1.02288 13.807L2.38593 11.284C2.49818 11.0762 2.55431 10.9724 2.57973 10.8725C2.60484 10.7738 2.61091 10.7027 2.60288 10.6012C2.59474 10.4984 2.54966 10.3647 2.45949 10.0972C2.21989 9.38633 2.09002 8.625 2.09002 7.83333C2.09002 3.92132 5.26134 0.75 9.17335 0.75C13.0854 0.75 16.2567 3.92132 16.2567 7.83333Z"
												style={{ stroke: 'var(--t-desc-gray)' }}
												stroke-width="1.5"
												stroke-linecap="round"
												stroke-linejoin="round"
											/>
										</svg>
									}
									label="Chat with us"
									onClick={() => {
										ChatService.open()
										setIsOpen(false)
									}}
								/>
								<ListItem
									leadingIcon={
										<svg
											xmlns="http://www.w3.org/2000/svg"
											width="17"
											height="17"
											viewBox="0 0 17 17"
											fill="none"
										>
											<path
												d="M15.75 5.75L15.75 0.75M15.75 0.75H10.75M15.75 0.75L9.08333 7.41667M6.58333 2.41667H4.75C3.34987 2.41667 2.6498 2.41667 2.11502 2.68915C1.64462 2.92883 1.26217 3.31129 1.02248 3.78169C0.75 4.31647 0.75 5.01654 0.75 6.41667V11.75C0.75 13.1501 0.75 13.8502 1.02248 14.385C1.26217 14.8554 1.64462 15.2378 2.11502 15.4775C2.6498 15.75 3.34987 15.75 4.75 15.75H10.0833C11.4835 15.75 12.1835 15.75 12.7183 15.4775C13.1887 15.2378 13.5712 14.8554 13.8108 14.385C14.0833 13.8502 14.0833 13.1501 14.0833 11.75V9.91667"
												style={{ stroke: 'var(--t-desc-gray)' }}
												stroke-width="1.5"
												stroke-linecap="round"
												stroke-linejoin="round"
											/>
										</svg>
									}
									label="See documentation"
									onClick={() => {
										window.open(AppConfig.KNOWLEDGEBASE_URL, '_blank')
										setIsOpen(false)
									}}
								/>
								<ListItem
									leadingIcon={
										<svg
											xmlns="http://www.w3.org/2000/svg"
											width="19"
											height="19"
											viewBox="0 0 19 19"
											fill="none"
										>
											<path
												d="M6.16667 11.3056C6.16667 12.3794 7.03722 13.25 8.11111 13.25H9.91667C11.0673 13.25 12 12.3173 12 11.1667C12 10.0161 11.0673 9.08333 9.91667 9.08333H8.25C7.09941 9.08333 6.16667 8.15059 6.16667 7C6.16667 5.84941 7.09941 4.91667 8.25 4.91667H10.0556C11.1294 4.91667 12 5.78722 12 6.86111M9.08333 3.66667V4.91667M9.08333 13.25V14.5M17.4167 9.08333C17.4167 13.6857 13.6857 17.4167 9.08333 17.4167C4.48096 17.4167 0.75 13.6857 0.75 9.08333C0.75 4.48096 4.48096 0.75 9.08333 0.75C13.6857 0.75 17.4167 4.48096 17.4167 9.08333Z"
												style={{ stroke: 'var(--t-desc-gray)' }}
												stroke-width="1.5"
												stroke-linecap="round"
												stroke-linejoin="round"
											/>
										</svg>
									}
									label="Affiliate program"
									onClick={() => {
										navigate('/workspace/affiliate?tab=about')
										setIsOpen(false)
									}}
								/>
								<Divider className="py-[2px]" />
							</>
						)}
						{user?.admin && (
							<>
								<ListItem
									leadingIcon={
										<svg
											xmlns="http://www.w3.org/2000/svg"
											width="19"
											height="19"
											viewBox="0 0 19 19"
											fill="none"
										>
											<path
												d="M17.4167 9.08333C17.4167 13.6857 13.6857 17.4167 9.08333 17.4167C4.48096 17.4167 0.75 13.6857 0.75 9.08333M17.4167 9.08333C17.4167 4.48096 13.6857 0.75 9.08333 0.75M17.4167 9.08333H15.3333M0.75 9.08333C0.75 4.48096 4.48096 0.75 9.08333 0.75M0.75 9.08333H2.83333M9.08333 0.75V2.83333M14.982 3.25L10.3333 7.83333M14.982 14.982L14.8121 14.8121C14.2356 14.2356 13.9473 13.9473 13.611 13.7412C13.3127 13.5584 12.9876 13.4238 12.6474 13.3421C12.2638 13.25 11.8562 13.25 11.0408 13.25L7.1258 13.25C6.3105 13.25 5.90285 13.25 5.51922 13.3421C5.17911 13.4238 4.85396 13.5585 4.55572 13.7412C4.21933 13.9474 3.93108 14.2356 3.35458 14.8121L3.18469 14.982M3.18469 3.25L4.63173 4.69704M10.75 9.08333C10.75 10.0038 10.0038 10.75 9.08333 10.75C8.16286 10.75 7.41667 10.0038 7.41667 9.08333C7.41667 8.16286 8.16286 7.41667 9.08333 7.41667C10.0038 7.41667 10.75 8.16286 10.75 9.08333Z"
												style={{ stroke: 'var(--t-desc-gray)' }}
												stroke-width="1.5"
												stroke-linecap="round"
												stroke-linejoin="round"
											/>
										</svg>
									}
									label="Admin Panel"
									onClick={() => {
										accountStore.clearAccount()
										navigate('/admin')
										setIsOpen(false)
									}}
								/>
								<Divider className="py-[2px]" />
							</>
						)}
						<ListItem
							leadingIcon={
								<svg
									xmlns="http://www.w3.org/2000/svg"
									width="17"
									height="16"
									viewBox="0 0 17 16"
									fill="none"
								>
									<path
										d="M11.5833 12.0979L15.75 7.93125M15.75 7.93125L11.5833 3.76458M15.75 7.93125H5.75M8.25 12.0979C8.25 12.3442 8.25 12.4674 8.24084 12.5741C8.14569 13.6829 7.32905 14.5953 6.23753 14.8123C6.13252 14.8332 6.01001 14.8468 5.76529 14.874L4.91412 14.9686C3.6354 15.1106 2.99601 15.1817 2.48805 15.0191C1.81078 14.8024 1.25785 14.3075 0.967649 13.6583C0.75 13.1714 0.75 12.5281 0.75 11.2415V4.62099C0.75 3.33438 0.75 2.69108 0.967649 2.20418C1.25785 1.55498 1.81078 1.06008 2.48805 0.843351C2.99601 0.680803 3.63538 0.751844 4.91412 0.893926L5.76529 0.988501C6.0101 1.0157 6.1325 1.0293 6.23753 1.05018C7.32905 1.26721 8.14569 2.17961 8.24084 3.28842C8.25 3.39511 8.25 3.51826 8.25 3.76458"
										style={{ stroke: 'var(--t-desc-gray)' }}
										strokeWidth="1.5"
										strokeLinecap="round"
										strokeLinejoin="round"
									/>
								</svg>
							}
							label="Log out"
							onClick={() => {
								removeFromLocalStorage(['selected_account', 'selected_asset'])
								clearFiltersFromStorage()
								clearMarketingParams()
								logout()
								removeImpersonationId()
							}}
						/>
					</MenuList>
				</div>
			)}
		</div>
	)
}
